package components.accounting

import components.project.LocalStorageKeys
import components.project.ServerProjectPreviewsTable
import components.project.projectsFiltering
import it.neckar.commons.kotlin.js.LocalStorageKey
import it.neckar.commons.kotlin.js.LocalStorageKeyPrefix
import it.neckar.commons.kotlin.js.LocalStorageSupport
import it.neckar.lizergy.model.company.CompanyCode
import it.neckar.lizergy.model.project.previews.AccountingProjectPreview
import it.neckar.lizergy.model.project.previews.AccountingResolvedProjectPreview
import it.neckar.lizergy.model.project.previews.ProjectQueryForUser
import it.neckar.lizergy.model.project.process.state.LizergyProcessStates
import it.neckar.lizergy.model.project.process.state.LizergyProcessStatesBrowserSerializer
import it.neckar.open.concurrent.debounce
import it.neckar.open.kotlin.lang.divFloor
import it.neckar.react.common.*
import it.neckar.react.common.router.*
import it.neckar.react.common.table.*
import it.neckar.user.UserLoginName
import kotlinx.serialization.builtins.serializer
import react.*
import react.dom.*
import router.RouterUrls
import router.useDocumentTitle
import services.UiActions
import services.http.AccountingProjectQueryRequest
import services.http.AccountingQuerySelection
import services.http.CompanyRevenue
import services.http.ProjectQuerySortedBy
import services.http.ProjectQuerySorting
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectCompanyResolver

val AccountingAndRevenueOverview: FC<Props> = fc("AccountingAndRevenueOverview") {
  val navigate = useNavigateUrl()
  val loggedInUser = useRequireLoggedInUser()
  val companyResolver = useSelectCompanyResolver()
  val companyName = useRequireCompanyForLoggedInUser().simpleName

  useDocumentTitle(companyName, "Rechnungen")

  val keyPrefix = LocalStorageKeyPrefix("AccountingAndRevenueOverview")

  val filteredByCompanyCode: StateInstance<CompanyCode?> = useState(null)

  // If the value is null, all projects shall be shown.
  val filteredByMaintainerName: StateInstance<UserLoginName?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.maintainer}"),
      serializer = UserLoginName.serializer()
    )
  )

  // If the value is null, all projects shall be shown.
  val filteredByEditorName: StateInstance<UserLoginName?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.editor}"),
      serializer = UserLoginName.serializer()
    )
  )

  val filteredByProjectState: StateInstance<LizergyProcessStates?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.processState}"),
      serializer = LizergyProcessStatesBrowserSerializer
    )
  )

  val filterInputStateProject = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.filterProject}"),
      serializer = String.serializer()
    ) ?: ""
  )
  val filterInputStateAddress = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.filterAddress}"),
      serializer = String.serializer()
    ) ?: ""
  )

  val sortedByFunction = useState(
    SortedByFunction(
      sortedBy = SortedBy.SortedDescending,
      tableDataColumn = ServerProjectPreviewsTable.getMostRecentProcessStateColumn<AccountingResolvedProjectPreview>(),
    )
  )

  val pageSize: StateInstance<Int?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalTableStorageKeys.pageSize}"),
      serializer = Int.serializer()
    ) ?: 15
  )

  val currentPage = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalTableStorageKeys.currentPage}"),
      serializer = Int.serializer()
    ) ?: 0
  )

  val visibleProjects: StateInstance<List<AccountingResolvedProjectPreview>?> = useState(null)
  val numberOfFilteredProjects: StateInstance<Int?> = useState(null)
  val numberOfRelevantProjects: StateInstance<Int?> = useState(null)

  val lizergyRevenue: StateInstance<CompanyRevenue?> = useState(null)
  val neckarITRevenue: StateInstance<CompanyRevenue?> = useState(null)
  val partnerRevenues: StateInstance<List<CompanyRevenue>?> = useState(null)


  val updateFilteredProjects = useRef(
    debounce<AccountingProjectQueryRequest>(coroutineScope = AppScope) { request ->
      visibleProjects.setter(null)
      numberOfFilteredProjects.setter(null)
      numberOfRelevantProjects.setter(null)

      UiActions.accountingQueryProjectPreviewsWithRevenues(request) { response ->
        if (response == null) return@accountingQueryProjectPreviewsWithRevenues
        val query = response.data
        visibleProjects.setter(query.visibleProjectPreviews)
        numberOfFilteredProjects.setter(query.numberOfTotalFilteredProjects)
        numberOfRelevantProjects.setter(query.numberOfTotalRelevantProjects)
        currentPage.setter { old ->
          val newCurrentPage = query.numberOfTotalFilteredProjects.divFloor(pageSize.value ?: 1)
          if (newCurrentPage == old) return@setter old
          old.coerceIn(0, newCurrentPage)
        }

        lizergyRevenue.setter(query.lizergyRevenue)
        neckarITRevenue.setter(query.neckarITRevenue)
        partnerRevenues.setter(query.partnerRevenues)
      }
    }
  )

  useMemo(
    filteredByCompanyCode.value,
    filteredByMaintainerName.value,
    filteredByEditorName.value,
    filterInputStateProject.value,
    filterInputStateAddress.value,
    filteredByProjectState.value,
    pageSize.value,
    currentPage.value,
    sortedByFunction.value,
  ) {
    val sorting = ProjectQuerySorting(
      sortedBy = when (sortedByFunction.value.sortedBy) {
        SortedBy.Unsorted -> ProjectQuerySortedBy.Unsorted
        SortedBy.SortedAscending -> ProjectQuerySortedBy.SortedAscending
        SortedBy.SortedDescending -> ProjectQuerySortedBy.SortedDescending
      },
      sortedColumn = ServerProjectPreviewsTable.parseTableColumnId(sortedByFunction.value.sortedColumn.id),
      projectQueryForUser = ProjectQueryForUser.Any,
      loggedInUser = loggedInUser.loginName,
    )

    val request = AccountingProjectQueryRequest(
      processStatesToFilter = LizergyProcessStates.allProcessStates,
      processStatesToHide = emptyList(),
      accountingQuerySelection = AccountingQuerySelection.Accounted,
      filteredByCompanyCode = filteredByCompanyCode.value,
      filteredByMaintainer = filteredByMaintainerName.value,
      filteredByEditor = filteredByEditorName.value,
      filterValueProject = filterInputStateProject.value,
      filterValueAddress = filterInputStateAddress.value,
      filterByProjectState = filteredByProjectState.value,
      indexOfFirstVisibleProject = pageSize.value?.let { currentPage.value * it } ?: 0,
      indexOfLastVisibleProject = pageSize.value?.let { (currentPage.value + 1) * it } ?: Int.MAX_VALUE,
      sorting = sorting,
      projectQueryComponent = null,
      howDoYouLikeYourQuoteElements = AccountingProjectPreview.QuoteElements.Frozen,
      loggedInUser = loggedInUser.loginName,
    )

    updateFilteredProjects.current?.let { it(request) }
  }


  div {

    h2 {
      +"Rechnungen"
    }

    InvoiceTable {
      attrs {
        this.lizergyRevenue = lizergyRevenue.value
        this.neckarITRevenue = neckarITRevenue.value
        this.partnerRevenues = partnerRevenues.value
      }
    }

    div {

      projectsFiltering(
        filteredByCompanyCode = filteredByCompanyCode,
        filteredByMaintainerName = filteredByMaintainerName,
        filteredByEditorName = filteredByEditorName,
        filteredByProjectState = filteredByProjectState,
        filterInputStateProject = filterInputStateProject,
        filterInputStateAddress = filterInputStateAddress,
        processStatesToFilter = LizergyProcessStates.allProcessStates.reversed(),
        localStorageKeyPrefix = keyPrefix,
      )

      busyIfNull(visibleProjects.value) { loadedProjectPreviews ->
        remoteTableWithPagination(
          tableClasses = "table table-striped table-responsive",
          entries = loadedProjectPreviews,
          columns = buildList {
            add(ServerProjectPreviewsTable.editButtonFirstColumn(loggedInUser) { navigate(RouterUrls.project(it).configuration((it.currentConfigurationPreview ?: return@editButtonFirstColumn).configurationId).earnings) })
            add(getAccountingProjectColumnWithComments(companyResolver, loggedInUser))
            add(accountingTable(howDoYouLikeYourQuoteElements = AccountingProjectPreview.QuoteElements.Frozen))
            add(ServerProjectPreviewsTable.getMostRecentProcessStateColumn())
          },
          pageSize = pageSize,
          currentPage = currentPage,
          numberOfTotalFilteredProjects = numberOfFilteredProjects.value ?: 0,
          sortedByFunction = sortedByFunction,
          keyPrefix = keyPrefix,
        )
      }

      div("mt-1") {
        span("form-text") {
          +"${numberOfFilteredProjects.value ?: "-"} von ${numberOfRelevantProjects.value ?: "-"} Projekten ausgewählt"
        }
      }

    }

  }

}
